import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, Users, Shield, Key, Plus, Pencil, Trash2, Loader2, 
  ChevronRight, UserCog, Search, Check, X, RefreshCw, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTimezone } from '@/contexts/TimezoneContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapeamento de níveis para cores
const levelColors = {
  100: 'bg-red-500/10 text-red-600 border-red-500/20',
  40: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  30: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  20: 'bg-green-500/10 text-green-600 border-green-500/20',
  10: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const levelNames = {
  100: 'Administrador',
  40: 'Diretor',
  30: 'Gerente',
  20: 'Supervisor',
  10: 'Operador',
};

// Format last login in a user-friendly way
const formatLastLogin = (dateString, formatDateTime) => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `${diffDays} dias atrás`;
  
  // For older dates, show the actual date
  return formatDateTime(dateString, { omitSeconds: true });
};

const AccessControlPage = () => {
  const { isAdmin } = useAuth();
  const { formatDateTime } = useTimezone();
  
  const [activeTab, setActiveTab] = useState('departments');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [equipmentGroups, setEquipmentGroups] = useState([]);
  
  // Dialog states
  const [departmentDialog, setDepartmentDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [userRbacDialog, setUserRbacDialog] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [editingUserRbac, setEditingUserRbac] = useState(null);
  
  // Form states
  const [departmentForm, setDepartmentForm] = useState({ name: '', code: '', description: '' });
  const [roleForm, setRoleForm] = useState({ name: '', code: '', description: '', level: 10, permissions: [] });
  const [userRbacForm, setUserRbacForm] = useState({ department_id: '', role_ids: [], equipment_group_ids: [], equipment_access: 'none' });
  
  // Saving states
  const [saving, setSaving] = useState(false);
  
  // Search/filter
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [deptRes, rolesRes, permsRes, usersRes, eqGroupsRes] = await Promise.all([
        axios.get(`${API}/rbac/departments`),
        axios.get(`${API}/rbac/roles`),
        axios.get(`${API}/rbac/permissions`),
        axios.get(`${API}/users`),
        axios.get(`${API}/rbac/equipment-groups`).catch(() => ({ data: [] })),
      ]);
      setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      setPermissions(Array.isArray(permsRes.data) ? permsRes.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setEquipmentGroups(Array.isArray(eqGroupsRes.data) ? eqGroupsRes.data : []);
    } catch (error) {
      console.error('Error fetching RBAC data:', error);
      toast.error('Erro ao carregar dados de controle de acesso');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAuditLogs = async () => {
    try {
      const res = await axios.get(`${API}/rbac/audit-logs?limit=50`);
      setAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Group permissions by resource
  const permissionsByResource = permissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) {
      acc[perm.resource] = [];
    }
    acc[perm.resource].push(perm);
    return acc;
  }, {});

  // Department handlers
  const handleOpenDepartmentDialog = (dept = null) => {
    if (dept) {
      setEditingDepartment(dept);
      setDepartmentForm({ name: dept.name, code: dept.code, description: dept.description || '' });
    } else {
      setEditingDepartment(null);
      setDepartmentForm({ name: '', code: '', description: '' });
    }
    setDepartmentDialog(true);
  };

  const handleSaveDepartment = async () => {
    if (!departmentForm.name || !departmentForm.code) {
      toast.error('Nome e código são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      if (editingDepartment) {
        await axios.put(`${API}/rbac/departments/${editingDepartment.id}`, departmentForm);
        toast.success('Departamento atualizado');
      } else {
        await axios.post(`${API}/rbac/departments`, departmentForm);
        toast.success('Departamento criado');
      }
      setDepartmentDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving department:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar departamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDepartment = async (dept) => {
    if (!window.confirm(`Desativar departamento "${dept.name}"?`)) return;
    try {
      await axios.delete(`${API}/rbac/departments/${dept.id}`);
      toast.success('Departamento desativado');
      fetchData();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.response?.data?.detail || 'Erro ao desativar departamento');
    }
  };

  // Role handlers
  const handleOpenRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        code: role.code,
        description: role.description || '',
        level: role.level,
        permissions: role.permissions || [],
      });
    } else {
      setEditingRole(null);
      setRoleForm({ name: '', code: '', description: '', level: 10, permissions: [] });
    }
    setRoleDialog(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name || !roleForm.code) {
      toast.error('Nome e código são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      if (editingRole) {
        await axios.put(`${API}/rbac/roles/${editingRole.id}`, roleForm);
        toast.success('Papel atualizado');
      } else {
        await axios.post(`${API}/rbac/roles`, roleForm);
        toast.success('Papel criado');
      }
      setRoleDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar papel');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.code === 'ADMIN') {
      toast.error('Não é possível excluir o papel ADMIN');
      return;
    }
    if (!window.confirm(`Desativar papel "${role.name}"?`)) return;
    try {
      await axios.delete(`${API}/rbac/roles/${role.id}`);
      toast.success('Papel desativado');
      fetchData();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.detail || 'Erro ao desativar papel');
    }
  };

  // Toggle permission directly from matrix
  const handleToggleMatrixPermission = async (role, permCode) => {
    if (role.code === 'ADMIN') {
      toast.error('Não é possível modificar o papel ADMIN');
      return;
    }
    
    const currentPermissions = role.permissions || [];
    const hasPermission = currentPermissions.includes(permCode);
    
    const newPermissions = hasPermission
      ? currentPermissions.filter(p => p !== permCode)
      : [...currentPermissions, permCode];
    
    try {
      await axios.put(`${API}/rbac/roles/${role.id}`, {
        ...role,
        permissions: newPermissions,
      });
      
      // Update local state immediately for responsiveness
      setRoles(prevRoles => 
        prevRoles.map(r => 
          r.id === role.id 
            ? { ...r, permissions: newPermissions }
            : r
        )
      );
      
      toast.success(`Permissão ${hasPermission ? 'removida' : 'adicionada'}`);
    } catch (error) {
      console.error('Error updating role permission:', error);
      toast.error('Erro ao atualizar permissão');
      // Refresh data to ensure consistency
      fetchData();
    }
  };

  const togglePermission = (permCode) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permCode)
        ? prev.permissions.filter(p => p !== permCode)
        : [...prev.permissions, permCode]
    }));
  };

  // User RBAC handlers
  const handleOpenUserRbacDialog = async (user) => {
    try {
      const res = await axios.get(`${API}/rbac/users/${user.id}/rbac`);
      setEditingUserRbac({ ...user, ...res.data });
      setUserRbacForm({
        department_id: res.data.department_id || '',
        role_ids: res.data.role_ids || [],
        equipment_group_ids: res.data.equipment_group_ids || [],
        equipment_access: res.data.equipment_access || 'none',
      });
      setUserRbacDialog(true);
    } catch (error) {
      console.error('Error fetching user RBAC:', error);
      toast.error('Erro ao carregar dados do usuário');
    }
  };

  const handleSaveUserRbac = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/rbac/users/${editingUserRbac.id}/rbac`, {
        department_id: userRbacForm.department_id || null,
        role_ids: userRbacForm.role_ids,
        equipment_group_ids: userRbacForm.equipment_group_ids,
        equipment_access: userRbacForm.equipment_access,
      });
      toast.success('Permissões do usuário atualizadas');
      setUserRbacDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving user RBAC:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserRole = (roleId) => {
    setUserRbacForm(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  const toggleEquipmentGroup = (groupId) => {
    setUserRbacForm(prev => {
      const newGroupIds = prev.equipment_group_ids.includes(groupId)
        ? prev.equipment_group_ids.filter(id => id !== groupId)
        : [...prev.equipment_group_ids, groupId];
      return {
        ...prev,
        equipment_group_ids: newGroupIds,
        equipment_access: newGroupIds.length > 0 ? 'groups' : 'none',
      };
    });
  };

  // Filter users
  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.includes(searchTerm) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Acesso restrito a administradores</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container space-y-6" data-testid="access-control-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Controle de Acesso</h1>
          <p className="text-muted-foreground">Gerencie departamentos, papéis e permissões de usuários</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-rbac">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{departments.length}</p>
                <p className="text-sm text-muted-foreground">Departamentos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Shield className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{roles.length}</p>
                <p className="text-sm text-muted-foreground">Papéis</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Key className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{permissions.length}</p>
                <p className="text-sm text-muted-foreground">Permissões</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Users className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-muted-foreground">Usuários</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="departments" className="gap-2" data-testid="tab-departments">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Departamentos</span>
          </TabsTrigger>
          <TabsTrigger value="roles" className="gap-2" data-testid="tab-roles">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Papéis</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
            <UserCog className="w-4 h-4" />
            <span className="hidden sm:inline">Usuários</span>
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2" onClick={fetchAuditLogs} data-testid="tab-audit">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Auditoria</span>
          </TabsTrigger>
        </TabsList>

        {/* Departments Tab */}
        <TabsContent value="departments" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Departamentos</h2>
            <Button onClick={() => handleOpenDepartmentDialog()} data-testid="add-department-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Departamento
            </Button>
          </div>
          
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-center">Usuários</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.map((dept) => (
                  <TableRow key={dept.id} data-testid={`dept-row-${dept.code}`}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{dept.code}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{dept.name}</TableCell>
                    <TableCell className="text-muted-foreground">{dept.description || '-'}</TableCell>
                    <TableCell className="text-center">{dept.user_count || 0}</TableCell>
                    <TableCell className="text-center">
                      {dept.is_active !== false ? (
                        <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Ativo</Badge>
                      ) : (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDepartmentDialog(dept)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDepartment(dept)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-6 mt-4">
          {/* Roles Table */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Papéis</h2>
              <Button onClick={() => handleOpenRoleDialog()} data-testid="add-role-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Papel
              </Button>
            </div>
            
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Nível</TableHead>
                    <TableHead className="text-center">Permissões</TableHead>
                    <TableHead className="text-center">Usuários</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id} data-testid={`role-row-${role.code}`}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">{role.code}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell>
                        <Badge className={levelColors[role.level] || 'bg-gray-100'}>
                          {levelNames[role.level] || role.level}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {role.permissions?.includes('*:*') ? (
                          <Badge className="bg-red-500/10 text-red-600">Todas</Badge>
                        ) : (
                          <span className="text-muted-foreground">{role.permissions?.length || 0}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{role.user_count || 0}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenRoleDialog(role)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {role.code !== 'ADMIN' && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Permissions Matrix */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Matriz de Permissões
                </h2>
                <p className="text-sm text-muted-foreground">
                  Visualize e edite as permissões de cada papel rapidamente
                </p>
              </div>
              <Badge variant="outline">{permissions.length} permissões</Badge>
            </div>

            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-background z-10 min-w-[200px]">Permissão</TableHead>
                      {roles.map((role) => (
                        <TableHead key={role.id} className="text-center min-w-[100px]">
                          <div className="flex flex-col items-center gap-1">
                            <Badge className={`${levelColors[role.level]} text-xs`}>
                              {role.code}
                            </Badge>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(permissionsByResource).map(([resource, perms]) => (
                      <React.Fragment key={resource}>
                        {/* Resource Header Row */}
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={roles.length + 1} className="sticky left-0 bg-muted/30 z-10">
                            <span className="font-semibold capitalize flex items-center gap-2">
                              <ChevronRight className="w-4 h-4" />
                              {resource}
                            </span>
                          </TableCell>
                        </TableRow>
                        {/* Permission Rows */}
                        {perms.map((perm) => (
                          <TableRow key={perm.code} className="hover:bg-muted/20">
                            <TableCell className="sticky left-0 bg-background z-10">
                              <div className="flex items-center gap-2">
                                <span className="text-sm">{perm.action}</span>
                                <Badge variant="outline" className="font-mono text-xs opacity-60">
                                  {perm.code}
                                </Badge>
                              </div>
                            </TableCell>
                            {roles.map((role) => {
                              const hasPermission = role.permissions?.includes('*:*') || 
                                                    role.permissions?.includes(perm.code);
                              const isAdmin = role.code === 'ADMIN';
                              
                              return (
                                <TableCell key={role.id} className="text-center">
                                  {isAdmin ? (
                                    <div className="flex justify-center">
                                      <Check className="w-5 h-5 text-green-500" />
                                    </div>
                                  ) : (
                                    <Checkbox
                                      checked={hasPermission}
                                      onCheckedChange={() => handleToggleMatrixPermission(role, perm.code)}
                                      className="mx-auto"
                                      data-testid={`matrix-${role.code}-${perm.code}`}
                                    />
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4 mt-4">
          <div className="flex justify-between items-center gap-4">
            <h2 className="text-lg font-semibold">Gerenciar Acessos de Usuários</h2>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar usuário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-users"
              />
            </div>
          </div>
          
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Último Login</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Departamento</TableHead>
                  <TableHead>Papéis</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.full_name}</TableCell>
                    <TableCell>
                      {user.last_login ? (
                        <span className="text-sm text-muted-foreground">
                          {formatLastLogin(user.last_login, formatDateTime)}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground italic">Nunca</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{user.username}</TableCell>
                    <TableCell>
                      {user.department_id ? (
                        <Badge variant="outline">
                          {departments.find(d => d.id === user.department_id)?.name || 'N/A'}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.role === 'admin' && !user.role_ids?.length ? (
                        <Badge className="bg-red-500/10 text-red-600">Admin (Legado)</Badge>
                      ) : user.role_ids?.length > 0 ? (
                        <div className="flex gap-1 flex-wrap">
                          {user.role_ids.slice(0, 2).map(rid => {
                            const role = roles.find(r => r.id === rid);
                            return role ? (
                              <Badge key={rid} variant="outline" className="text-xs">{role.code}</Badge>
                            ) : null;
                          })}
                          {user.role_ids.length > 2 && (
                            <Badge variant="secondary" className="text-xs">+{user.role_ids.length - 2}</Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleOpenUserRbacDialog(user)}
                        data-testid={`edit-user-rbac-${user.id}`}
                      >
                        <UserCog className="w-4 h-4 mr-2" />
                        Gerenciar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Log de Auditoria</h2>
            <Button variant="outline" size="sm" onClick={fetchAuditLogs}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
          
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Recurso</TableHead>
                  <TableHead>Detalhes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">
                      {formatDateTime(log.timestamp)}
                    </TableCell>
                    <TableCell className="font-medium">{log.user_name}</TableCell>
                    <TableCell>
                      <Badge variant={log.action === 'delete' ? 'destructive' : 'outline'}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">{log.resource_type}</span>
                      {log.resource_name && (
                        <span className="ml-1 font-medium">({log.resource_name})</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                      {log.changes ? JSON.stringify(log.changes).substring(0, 50) + '...' : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {auditLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Department Dialog */}
      <Dialog open={departmentDialog} onOpenChange={setDepartmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDepartment ? 'Editar Departamento' : 'Novo Departamento'}</DialogTitle>
            <DialogDescription>
              {editingDepartment ? 'Atualize as informações do departamento' : 'Preencha os dados do novo departamento'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dept-name">Nome *</Label>
              <Input
                id="dept-name"
                value={departmentForm.name}
                onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                placeholder="Ex: Departamento de TI"
                data-testid="dept-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dept-code">Código * (3 letras)</Label>
              <Input
                id="dept-code"
                value={departmentForm.code}
                onChange={(e) => setDepartmentForm({ ...departmentForm, code: e.target.value.toUpperCase() })}
                placeholder="Ex: TI"
                maxLength={5}
                disabled={!!editingDepartment}
                data-testid="dept-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dept-desc">Descrição</Label>
              <Input
                id="dept-desc"
                value={departmentForm.description}
                onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                placeholder="Descrição opcional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepartmentDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveDepartment} disabled={saving} data-testid="save-dept-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Editar Papel' : 'Novo Papel'}</DialogTitle>
            <DialogDescription>
              {editingRole ? 'Atualize as informações e permissões do papel' : 'Configure o novo papel e suas permissões'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role-name">Nome *</Label>
                <Input
                  id="role-name"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  placeholder="Ex: Supervisor de Campo"
                  data-testid="role-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role-code">Código *</Label>
                <Input
                  id="role-code"
                  value={roleForm.code}
                  onChange={(e) => setRoleForm({ ...roleForm, code: e.target.value.toUpperCase().replace(/\s/g, '_') })}
                  placeholder="Ex: SUPERVISOR_CAMPO"
                  disabled={!!editingRole}
                  data-testid="role-code-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role-desc">Descrição</Label>
              <Input
                id="role-desc"
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="Descrição do papel"
              />
            </div>
            <div className="space-y-2">
              <Label>Nível Hierárquico</Label>
              <Select
                value={String(roleForm.level)}
                onValueChange={(v) => setRoleForm({ ...roleForm, level: parseInt(v) })}
              >
                <SelectTrigger data-testid="role-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Operador (10)</SelectItem>
                  <SelectItem value="20">Supervisor (20)</SelectItem>
                  <SelectItem value="30">Gerente (30)</SelectItem>
                  <SelectItem value="40">Diretor (40)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label>Permissões</Label>
              <p className="text-sm text-muted-foreground">Selecione as permissões que este papel terá</p>
              
              <ScrollArea className="h-[300px] border rounded-md p-4">
                {Object.entries(permissionsByResource).map(([resource, perms]) => (
                  <div key={resource} className="mb-4">
                    <h4 className="font-medium capitalize mb-2 flex items-center gap-2">
                      <ChevronRight className="w-4 h-4" />
                      {resource}
                    </h4>
                    <div className="grid grid-cols-2 gap-2 ml-6">
                      {perms.map((perm) => (
                        <div key={perm.code} className="flex items-center space-x-2">
                          <Checkbox
                            id={perm.code}
                            checked={roleForm.permissions.includes(perm.code)}
                            onCheckedChange={() => togglePermission(perm.code)}
                          />
                          <label
                            htmlFor={perm.code}
                            className="text-sm cursor-pointer"
                          >
                            {perm.action}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </ScrollArea>
              
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-muted-foreground">
                  {roleForm.permissions.length} permissões selecionadas
                </span>
                <div className="space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRoleForm({ ...roleForm, permissions: [] })}
                  >
                    Limpar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRoleForm({ ...roleForm, permissions: permissions.map(p => p.code) })}
                  >
                    Selecionar Todas
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveRole} disabled={saving} data-testid="save-role-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User RBAC Dialog */}
      <Dialog open={userRbacDialog} onOpenChange={setUserRbacDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Gerenciar Acessos</DialogTitle>
            <DialogDescription>
              Configure departamento e papéis para {editingUserRbac?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{editingUserRbac?.full_name}</p>
                  <p className="text-sm text-muted-foreground font-mono">{editingUserRbac?.username}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Departamento</Label>
              <Select
                value={userRbacForm.department_id || 'none'}
                onValueChange={(v) => setUserRbacForm({ ...userRbacForm, department_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="user-dept-select">
                  <SelectValue placeholder="Selecione um departamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name} ({dept.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Papéis</Label>
              <p className="text-sm text-muted-foreground">Selecione um ou mais papéis</p>
              <div className="border rounded-md p-3 space-y-2 max-h-[200px] overflow-y-auto">
                {roles.map((role) => (
                  <div key={role.id} className="flex items-center space-x-3 p-2 hover:bg-muted/50 rounded">
                    <Checkbox
                      id={`role-${role.id}`}
                      checked={userRbacForm.role_ids.includes(role.id)}
                      onCheckedChange={() => toggleUserRole(role.id)}
                    />
                    <label htmlFor={`role-${role.id}`} className="flex-1 cursor-pointer">
                      <span className="font-medium">{role.name}</span>
                      <Badge className={`ml-2 ${levelColors[role.level]}`}>
                        {levelNames[role.level]}
                      </Badge>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Grupos de Equipamentos</Label>
              <p className="text-sm text-muted-foreground">
                Selecione os grupos de equipamentos que o usuário pode visualizar
              </p>
              {editingUserRbac?.role === 'admin' ? (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-sm text-blue-400">
                    Administradores têm acesso total a todos os equipamentos.
                  </p>
                </div>
              ) : (
                <div className="border rounded-md p-3 space-y-2 max-h-[200px] overflow-y-auto">
                  {equipmentGroups.filter(g => g.is_active !== false).map((group) => (
                    <div key={group.id} className="flex items-center space-x-3 p-2 hover:bg-muted/50 rounded">
                      <Checkbox
                        id={`eqgroup-${group.id}`}
                        checked={userRbacForm.equipment_group_ids.includes(group.id)}
                        onCheckedChange={() => toggleEquipmentGroup(group.id)}
                      />
                      <label htmlFor={`eqgroup-${group.id}`} className="flex-1 cursor-pointer">
                        <span className="font-medium">{group.name}</span>
                        <span className="text-sm text-muted-foreground ml-2">
                          ({group.equipment_count || 0} equip.)
                        </span>
                      </label>
                    </div>
                  ))}
                  {equipmentGroups.filter(g => g.is_active !== false).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Nenhum grupo de equipamentos cadastrado
                    </p>
                  )}
                </div>
              )}
              {userRbacForm.equipment_group_ids.length === 0 && editingUserRbac?.role !== 'admin' && (
                <p className="text-xs text-yellow-500">
                  Sem grupos selecionados — usuário não verá nenhum equipamento.
                </p>
              )}
            </div>

            {editingUserRbac?.role === 'admin' && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-700">
                  <strong>Nota:</strong> Este usuário tem o papel de Admin (legado) que garante acesso total.
                  Os papéis RBAC são adicionais.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserRbacDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveUserRbac} disabled={saving} data-testid="save-user-rbac-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessControlPage;
